import type { ComponentConfig } from '@measured/puck';
import { ArrowRight } from 'lucide-react';
import { Container } from '../layout/Container';
import { Button } from '../ui/button';
import { Reveal } from '../motion/Reveal';

interface CTAFinalProps {
  title?: string;
  description?: string;
  ctaLabel?: string;
  ctaHref?: string;
  secondaryLabel?: string;
  secondaryHref?: string;
}

export function CTAFinal({
  title = 'Une seule question : quelles aides pour vos travaux ?',
  description = 'Outils gratuits pour estimer vos aides et primes selon votre profil — sans engagement, sans téléphone à donner. Vous repartez avec une fourchette chiffrée et la liste des dispositifs accessibles.',
  ctaLabel = 'Voir nos outils gratuits',
  ctaHref = '/outils',
  secondaryLabel = 'Parler à un conseiller',
  secondaryHref = '/contact',
}: CTAFinalProps) {
  return (
    <section className="relative overflow-hidden bg-ink py-24 md:py-32">
      {/* Mesh decorative — ink background avec gradients chauds */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-mesh-dark opacity-100" />
        <div className="absolute -top-1/2 left-1/4 h-[600px] w-[600px] rounded-full bg-accent-500/20 blur-[120px]" />
        <div className="absolute -bottom-1/2 right-1/4 h-[600px] w-[600px] rounded-full bg-brand-500/30 blur-[120px]" />
        <div className="absolute inset-0 bg-noise opacity-[0.06] mix-blend-overlay" />
      </div>

      <Container className="relative">
        <Reveal>
          <div className="mx-auto max-w-3xl text-center">
            <h2
              className="h-section text-balance"
              style={{
                backgroundImage: 'linear-gradient(120deg, #ffffff 0%, #d1fae5 45%, #84cc16 100%)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                color: 'transparent',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {title}
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-stone-300 md:text-xl">{description}</p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
              <Button asChild size="lg" variant="accent" className="group">
                <a href={ctaHref}>
                  {ctaLabel}
                  <ArrowRight className="h-4 w-4 transition-transform duration-350 ease-stripe group-hover/btn:translate-x-1" />
                </a>
              </Button>
              <Button asChild size="lg" variant="dark-outline">
                <a href={secondaryHref}>{secondaryLabel}</a>
              </Button>
            </div>
            <p className="mt-7 text-xs uppercase tracking-[0.18em] text-stone-500">
              Aucun engagement, Aucune carte requise, Sources officielles
            </p>
          </div>
        </Reveal>
      </Container>
    </section>
  );
}

export const CTAFinalConfig: ComponentConfig<CTAFinalProps> = {
  fields: {
    title: { type: 'textarea' },
    description: { type: 'textarea' },
    ctaLabel: { type: 'text' },
    ctaHref: { type: 'text' },
    secondaryLabel: { type: 'text' },
    secondaryHref: { type: 'text' },
  },
  defaultProps: {
    title: 'Une seule question : quelles aides pour vos travaux ?',
    description:
      'Outils gratuits pour estimer vos aides et primes selon votre profil — sans engagement, sans téléphone à donner. Vous repartez avec une fourchette chiffrée et la liste des dispositifs accessibles.',
    ctaLabel: 'Voir nos outils gratuits',
    ctaHref: '/outils',
    secondaryLabel: 'Parler à un conseiller',
    secondaryHref: '/contact',
  },
  render: CTAFinal,
};
